import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import SelectField from "../../../../components/ui/SelectField";
import { getGoalSummary } from "../../../../goals/GoalList";
import ReportSettingAction from "../../../../actions/ReportSettingAction";
import { FilterRuleProps } from "../../../../main/component/filter/AdvancedFilter";
import FilterButton from "./FilterButton";
import FilterTag from "./FilterTag";
import { AdvancedFilterTag } from "./AdvancedFilterPanel";

import { supportedFieldTypes } from "../../../../main/component/filter/FilterDropdown";

/**
 * Convert professional filter format (from Redux) to FilterRuleProps[]
 * Professional format: { fieldId: { operator, values, dataType? } }
 * FilterRuleProps: { operator, fieldType, fieldId, fieldValue }
 */
const professionalToFilterRules = (professionalFilters: any): FilterRuleProps[] => {
  if (!professionalFilters || Object.keys(professionalFilters).length === 0) {
    return [];
  }

  const rules: FilterRuleProps[] = [];
  Object.entries(professionalFilters).forEach(([fieldId, def]: [string, any]) => {
    if (!def || !def.values) return;
    const operator = def.operator || "is";
    const values = Array.isArray(def.values) ? def.values : [];

    if (operator === "dateRange") {
      // Date range filters: keep as single rule with 2 values
      rules.push({
        operator: "dateRange",
        fieldType: "datetime",
        fieldId,
        fieldValue: values,
      });
    } else {
      // Select/text filters: use "in" operator with all values
      if (values.length > 0) {
        rules.push({
          operator: "in",
          fieldType: def.dataType || "stringArray",
          fieldId,
          fieldValue: values.map((v: any) => String(v)),
        });
      }
    }
  });

  return rules;
};

/**
 * Convert FilterRuleProps[] back to professional filter format for the report Redux store
 */
const filterRulesToProfessional = (rules: FilterRuleProps[]): any => {
  const filters: any = {};
  rules.forEach((rule) => {
    if (!rule.fieldId) return;

    if (rule.operator === "dateRange") {
      // Date range stays as-is
      if (rule.fieldValue && rule.fieldValue.length > 0) {
        filters[rule.fieldId] = {
          operator: "dateRange",
          values: rule.fieldValue,
        };
      }
    } else {
      // For select/text: merge values for the same fieldId
      const existing = filters[rule.fieldId];
      const newValues = rule.fieldValue || [];
      if (existing) {
        existing.values = [...existing.values, ...newValues];
      } else if (newValues.length > 0) {
        filters[rule.fieldId] = {
          operator: rule.operator || "is",
          values: newValues.map((v: any) => String(v)),
        };
      }
    }
  });
  return filters;
};

interface FilterTabContentProps {
  objectType: string;
  chartType?: string;
}

const FilterTabContent = ({ objectType, chartType }: FilterTabContentProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const currency = useSelector((state: any) =>
    state.projectReducer?.getIn?.(["project", "currency"])
  );
  const reportData = useSelector((state: any) => state.reportSetting.get("data"));
  const goalId = reportData.get("goalId");
  const reportDataFilters = reportData.get("filters");

  const goals = useSelector((state: any) => state.goal?.get?.("goals"));
  const goal = goals.find((item: any) => item.get("id") === goalId);

  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));

  const availableFields = useMemo(() => {
    if (!fields || !fields.filter) return [];
    return fields
      .filter(
        (item: any) =>
          item.get("formType") === objectType && supportedFieldTypes.includes(item.get("type"))
      )
      .map((item: any) => ({
        label: item.get("label"),
        value: item.get("id"),
        data: item.toJS(),
      }))
      .toJS();
  }, [objectType, fields]);

  const [filterRules, setFilterRules] = useState<FilterRuleProps[]>([]);

  const goalOptions: Array<{ value: string; label: string }> = useMemo(() => {
    if (!goals || !goals.map) return [];
    return goals
      .map((item: any) => ({
        value: item.get("id"),
        label: getGoalSummary(item, currency, t, i18n),
      }))
      .toArray();
  }, [goals, currency, t, i18n]);

  // Sync filter rules from Redux store
  useEffect(() => {
    if (chartType) {
      const pf = reportDataFilters?.toJS?.() || {};
      const rules = professionalToFilterRules(pf);
      setFilterRules(rules);
    }
  }, [chartType, reportDataFilters]);

  // Dispatch filter changes to Redux
  const dispatchFilters = (rules: FilterRuleProps[], newGoalId?: string) => {
    const filters = filterRulesToProfessional(rules);
    dispatch(
      ReportSettingAction.settingDataReport({
        ...reportData.toJS(),
        filters,
        goalId: newGoalId ?? goalId,
      })
    );
  };

  const handleChange = (index: number, value: FilterRuleProps) => {
    const newRules = filterRules.map((item, idx) => (idx === index ? value : item));
    setFilterRules([...newRules]);
    // Only dispatch when the changed filter has values — dispatching incomplete
    // filters causes filterRulesToProfessional to skip them, then the Redux→state
    // sync overwrites local state and removes the incomplete filter.
    if (value.fieldId && value.operator && value.fieldValue && value.fieldValue.length > 0) {
      dispatchFilters(newRules);
    }
  };

  const handleAdd = (value: FilterRuleProps) => {
    const newRules = [...filterRules, value];
    setFilterRules(newRules);
    if (value.fieldId && value.operator && value.fieldValue && value.fieldValue.length > 0) {
      dispatchFilters(newRules);
    }
  };

  const handleRemove = (index: number) => {
    const newRules = filterRules.filter((_, idx) => idx !== index);
    setFilterRules([...newRules]);
    dispatchFilters(newRules);
  };

  const handleDeleteAll = () => {
    setFilterRules([]);
    dispatchFilters([]);
  };

  // Track previous filter count to detect 1→2+ transition
  const prevCountRef = useRef(filterRules.length);
  const justTransitioned = prevCountRef.current < 2 && filterRules.length >= 2;
  useEffect(() => {
    prevCountRef.current = filterRules.length;
  }, [filterRules.length]);

  // Goal chart type: keep the existing goal selector
  if (chartType === "Goal") {
    return (
      <div className="space-y-5">
        <SelectField
          label={t("Goal")}
          placeholder={t("Select goal")}
          value={goalOptions.find((item) => item.value === goalId)}
          options={goalOptions}
          onChange={(opt) => {
            dispatchFilters(filterRules, opt?.value);
          }}
        />
        {goal && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t("Target Value")}</span>
                <span className="text-sm font-semibold text-gray-900">
                  {goal.get("trackingGoal")?.value
                    ? `${goal.get("trackingGoal").value} ${goal.get("trackingMetric") || ""}`
                    : t("Not set")}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t("Frequency")}</span>
                <span className="text-sm text-gray-600">
                  {goal.get("frequency") || t("Not specified")}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t("Tracking Period")}</span>
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  {goal.get("trackingFrom") && goal.get("trackingTo")
                    ? `${new Date(goal.get("trackingFrom")).toLocaleDateString()} - ${new Date(
                        goal.get("trackingTo")
                      ).toLocaleDateString()}`
                    : t("Not specified")}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t("Status")}</span>
                <span
                  className={`text-sm font-medium ${
                    goal.get("enabled") === true ? "text-green-600" : "text-gray-600"
                  }`}
                >
                  {goal.get("enabled") === true ? t("Active") : t("Inactive")}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t("Type")}</span>
                <span className="text-sm text-gray-600">{goal.get("type") || t("Unknown")}</span>
              </div>

              {goal.get("scope") && (
                <div className="pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-700 block mb-1">{t("Scope")}</span>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {goal.get("scope").value?.label || goal.get("scope").type || t("All")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main filter UI: progressive filter

  // 0 filters → compact "Add filter" button
  if (filterRules.length === 0) {
    return (
      <div className="space-y-3">
        <FilterButton fields={availableFields} onAdd={handleAdd} />
      </div>
    );
  }

  // 1 filter → inline tag with + button
  if (filterRules.length === 1) {
    return (
      <div className="space-y-3">
        <FilterTag
          fields={availableFields}
          rule={filterRules[0]}
          index={0}
          showLogic={false}
          showAdd={true}
          onChange={(r) => handleChange(0, r)}
          onRemove={() => handleRemove(0)}
          onAdd={handleAdd}
        />
      </div>
    );
  }

  // ≥2 filters → collapsed tag with floating wide popover
  return (
    <div className="space-y-3">
      <AdvancedFilterTag
        fields={availableFields}
        rules={filterRules}
        onChange={handleChange}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onClearAll={handleDeleteAll}
        defaultOpen={justTransitioned}
      />
    </div>
  );
};

export default FilterTabContent;
